import React, { useEffect, useRef, useState } from "react";
import { Search, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

// Convert full US/CA state names to 2-letter codes.
const STATE_MAP = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA", Kansas: "KS",
  Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD", Massachusetts: "MA",
  Michigan: "MI", Minnesota: "MN", Mississippi: "MS", Missouri: "MO", Montana: "MT",
  Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND",
  Ohio: "OH", Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI",
  "South Carolina": "SC", "South Dakota": "SD", Tennessee: "TN", Texas: "TX",
  Utah: "UT", Vermont: "VT", Virginia: "VA", Washington: "WA", "West Virginia": "WV",
  Wisconsin: "WI", Wyoming: "WY",
  Alberta: "AB", "British Columbia": "BC", Manitoba: "MB", "New Brunswick": "NB",
  "Newfoundland and Labrador": "NL", "Nova Scotia": "NS", Ontario: "ON",
  "Prince Edward Island": "PE", Quebec: "QC", Québec: "QC", Saskatchewan: "SK",
  "Northwest Territories": "NT", Nunavut: "NU", Yukon: "YT",
};

function extractAddress(item) {
  const a = item.address || {};
  const streetParts = [a.house_number, a.road || a.pedestrian || a.footway].filter(Boolean);
  const street = streetParts.join(" ") || a.building || a.amenity || "";
  const city = a.city || a.town || a.village || a.hamlet || a.municipality || a.suburb || "";
  const stateFull = a.state || a.province || "";
  const state = STATE_MAP[stateFull] || stateFull.slice(0, 3).toUpperCase();
  const zip = a.postcode || "";
  return { street, city, state, zip, display: item.display_name };
}

/**
 * Free address autocomplete using OpenStreetMap Nominatim.
 * As user types → suggestions appear after 300ms debounce.
 * Selecting a result calls onSelect({ street, city, state, zip, display }).
 * No API key required. 1 request/sec limit — plenty for form typing.
 */
export default function AddressLookup({ value, onChange, onSelect, placeholder = "Search address or business…", dataTestId }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const runSearch = (text) => {
    if (!text || text.trim().length < 3) { setResults([]); return; }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(text)}&format=json&addressdetails=1&limit=6&countrycodes=us,ca`;
    fetch(url, { headers: { "Accept-Language": "en" }, signal: abortRef.current.signal })
      .then((r) => r.json())
      .then((data) => { setResults(Array.isArray(data) ? data : []); setOpen(true); })
      .catch((err) => { if (err.name !== "AbortError") console.warn("addr lookup", err); })
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQ(val);
    onChange?.(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val), 350);
  };

  const pick = (item) => {
    const parsed = extractAddress(item);
    setQ(parsed.street || item.display_name);
    onChange?.(parsed.street || item.display_name);
    onSelect?.(parsed);
    setOpen(false);
    setResults([]);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
        <Input
          value={q}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder}
          className="sharp pl-8 pr-8 apex-input"
          data-testid={dataTestId}
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-zinc-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute left-0 top-full mt-1 z-50 w-full max-h-72 overflow-y-auto bg-white border border-zinc-950 shadow-lg text-xs">
          {results.map((r) => (
            <button
              type="button"
              key={r.place_id}
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2 border-b border-zinc-100 hover:bg-blue-50 flex items-start gap-2"
              data-testid={`addr-lookup-row-${r.place_id}`}
            >
              <MapPin className="h-3 w-3 mt-0.5 text-zinc-400 flex-shrink-0" />
              <span className="text-[12px] leading-snug">{r.display_name}</span>
            </button>
          ))}
          <div className="px-3 py-1.5 text-[9px] text-zinc-500 bg-zinc-50 font-mono">
            © OpenStreetMap contributors
          </div>
        </div>
      )}
    </div>
  );
}
